/** No input for this long counts as idle. */
const IDLE_AFTER_MS = 3 * 60 * 1000
/** How often an idle, visible tab still polls. */
const IDLE_POLL_INTERVAL_MS = 2 * 60 * 1000
/**
 * How long after a seen change every delta collection keeps pulling. A write
 * that committed late with an older server time does not move the change stamp,
 * so this window catches it, like the delta cursors' own overlap.
 */
const CHANGE_SETTLE_MS = 2 * 60 * 1000
/**
 * Longest gap between full delta pulls. Measurements are not in the change
 * stamp (their `updated_at` is client-set), so this bounds how stale they get.
 */
const QUIET_FULL_PULL_MS = 5 * 60 * 1000

type LightSyncPacingOptions = {
   now?: () => number
}

/**
 * Paces the lightweight background sync: polls every tick while the user is
 * active, slows to `IDLE_POLL_INTERVAL_MS` once idle, and pulls every delta
 * collection only when the change stamp moved (or recently moved, or a while
 * has passed), so a quiet poll costs one stamp check.
 *
 * @param options - An optional clock.
 * @returns The pacing methods.
 */
export function createLightSyncPacing(options: LightSyncPacingOptions = {}) {
   const now = options.now ?? Date.now
   let lastActivityAt = now()
   let lastTickAt = Number.NEGATIVE_INFINITY
   let lastChangeAt = Number.NEGATIVE_INFINITY
   let lastFullPullAt = Number.NEGATIVE_INFINITY

   /**
    * Returns whether the user has been inactive long enough to slow polling.
    *
    * @returns True when idle.
    */
   function isIdle() {
      return now() - lastActivityAt >= IDLE_AFTER_MS
   }

   return {
      /**
       * Records user input.
       *
       * @returns True when the user was idle, so the caller can sync right away.
       */
      markActivity() {
         const wasIdle = isIdle()
         lastActivityAt = now()
         return wasIdle
      },
      /**
       * Returns whether this interval tick should poll, and records it when so.
       *
       * @returns True when the tick should run.
       */
      takeTick() {
         if (isIdle() && now() - lastTickAt < IDLE_POLL_INTERVAL_MS) {
            return false
         }
         lastTickAt = now()
         return true
      },
      /**
       * Decides whether every delta collection should pull after a stamp check.
       *
       * @param upToDate - Whether the server reported no change since the client's stamp.
       * @returns True to pull all collections; false to sync only those with local work.
       */
      shouldPullAll(upToDate: boolean) {
         const at = now()
         if (!upToDate) {
            lastChangeAt = at
         }
         if (
            at - lastChangeAt < CHANGE_SETTLE_MS ||
            at - lastFullPullAt >= QUIET_FULL_PULL_MS
         ) {
            lastFullPullAt = at
            return true
         }
         return false
      },
   }
}
